/**
 * The authenticated chrome, composed once and used by every page:
 *
 *   ┌──────────┬────────────────────────────────┐
 *   │          │            Header              │
 *   │ Sidebar  ├────────────────────────────────┤
 *   │          │            TopNav              │
 *   │          ├────────────────────────────────┤
 *   │          │  content  ← the ONE scroller   │
 *   │          ├────────────────────────────────┤
 *   │          │            Footer              │
 *   └──────────┴────────────────────────────────┘
 *
 * Pages render only their own content — never their own header or scroll
 * container. That's why v1 pages had to guess heights like
 * `calc(100vh - 140px)`.
 */

import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import styles from "./AppShell.module.scss";
import { Header } from "../chrome/Header";
import { Sidebar } from "../navigation/Sidebar";
import { TopNav } from "../navigation/TopNav";
import { Footer } from "../chrome/Footer";
import { useNav } from "../navigation/useNav";
import { usePersistentState } from "@shared/hooks/usePersistentState";
import { AutoSelectClient } from "@features/endpoint-inventory/AutoSelectClient";
import { AutoSelectOrg }    from "@features/endpoint-inventory/AutoSelectOrg";
import { ScopeChangeRefresher } from "@features/endpoint-inventory/ScopeChangeRefresher";
import { AgentScopeGate } from "@features/endpoint-inventory/AgentScopeGate";
import { AlertsBell } from "@features/resource-alerts/components/AlertsBell";
import { useAuth } from "@features/auth/AuthContext";
import { useCan } from "@features/auth/hooks/useCan";
import { SHELL } from "@shared/config";

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Persisted so the sidebar keeps its collapsed/expanded choice across
  // reloads. Default false (expanded) on first visit / when nothing is stored.
  const [collapsed, setCollapsed] = usePersistentState("sidebar.collapsed", false);
  const { pathname } = useLocation();
  const { itemFor } = useNav();

  // The page title comes from the nav model, so it can't drift from the menu.
  // Memoized so the Header prop doesn't churn on every AppShell render.
  const title = useMemo(
    () => itemFor(pathname)?.label ?? SHELL.defaultTitle,
    [itemFor, pathname],
  );

  // Stable identity so a memoized Sidebar isn't forced to re-render each
  // AppShell tick just because we handed it a fresh arrow function.
  const onToggle = useCallback(() => setCollapsed((c) => !c), [setCollapsed]);

  // The alerts bell only makes sense for org-scoped users whose org has the
  // System Monitoring module and who can read alerts — otherwise the summary
  // endpoint 403s. Gate here so the Header stays chrome-only.
  const { user } = useAuth();
  const { can } = useCan();
  const showAlertsBell =
    user?.scope_level === "org" &&
    user?.modules?.sysmon === true &&
    can("alerts:read");

  // Viewers only have one sidebar entry (Endpoint Inventory). Rendering a
  // sidebar for just one item makes the chrome feel abandoned, so we hide the
  // whole rail and move that single destination into the top-right of the
  // TopNav (see TopNav.tsx). Non-viewers get the sidebar as a floating overlay
  // so expanding it slides over the content instead of squeezing the layout.
  const isViewer = user?.role === "viewer";

  return (
    <div className={styles.shell}>
      {/* Order matters: AutoSelectOrg writes specta.org_id for group admins
          BEFORE AutoSelectClient reads it (via useEffectiveOrgId) to pick an
          agent. For org admins AutoSelectOrg is a no-op. */}
      <AutoSelectOrg />
      <AutoSelectClient />
      {/* Refetches every query the moment the selected org or client
          changes, so the current page always reflects the active
          (org, client) — see ScopeChangeRefresher for the why. */}
      <ScopeChangeRefresher />
      {!isViewer && <Sidebar collapsed={collapsed} onToggle={onToggle} />}

      <div className={`${styles.main} ${!isViewer ? styles.mainWithRail : ""}`}>
        <Header title={title} bell={showAlertsBell ? <AlertsBell /> : undefined} showBell={showAlertsBell} />
        <TopNav />
        {/* Footer lives INSIDE the scroll container so it sits at the end of
            the page content rather than pinned to the viewport bottom. When
            content is short, .contentInner's flex:1 pushes the footer down
            to the bottom of the viewport anyway; when content overflows, the
            footer scrolls into view at the end — not visible until you reach it. */}
        <main className={styles.content}>
          {/* Agent-scoped pages render only when a client is selected, so a
              switch to an org with no agents shows an explicit empty state
              instead of the previous agent's data. */}
          <div className={styles.contentInner}>
            <AgentScopeGate>{children}</AgentScopeGate>
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

// /**
//  * The authenticated chrome, composed once and used by every page:
//  *
//  *   ┌──────────┬────────────────────────────────┐
//  *   │          │            Header              │
//  *   │ Sidebar  ├────────────────────────────────┤
//  *   │          │            TopNav              │
//  *   │          ├────────────────────────────────┤
//  *   │          │  content  ← the ONE scroller   │
//  *   │          ├────────────────────────────────┤
//  *   │          │            Footer              │
//  *   └──────────┴────────────────────────────────┘
//  *
//  * Pages render only their own content — never their own header or scroll
//  * container. That's why v1 pages had to guess heights like
//  * `calc(100vh - 140px)`.
//  */

// import { useCallback, useMemo } from "react";
// import { useLocation } from "react-router-dom";
// import styles from "./AppShell.module.scss";
// import { Header } from "../chrome/Header";
// import { Sidebar } from "../navigation/Sidebar";
// import { TopNav } from "../navigation/TopNav";
// import { Footer } from "../chrome/Footer";
// import { useNav } from "../navigation/useNav";
// import { usePersistentState } from "@shared/hooks/usePersistentState";
// import { AutoSelectClient } from "@features/endpoint-inventory/AutoSelectClient";
// import { AutoSelectOrg }    from "@features/endpoint-inventory/AutoSelectOrg";
// import { ScopeChangeRefresher } from "@features/endpoint-inventory/ScopeChangeRefresher";
// import { AlertsBell } from "@features/resource-alerts/components/AlertsBell";
// import { useAuth } from "@features/auth/AuthContext";
// import { useCan } from "@features/auth/hooks/useCan";
// import { SHELL } from "@shared/config";

// export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   // Persisted so the sidebar keeps its collapsed/expanded choice across
//   // reloads. Default false (expanded) on first visit / when nothing is stored.
//   const [collapsed, setCollapsed] = usePersistentState("sidebar.collapsed", false);
//   const { pathname } = useLocation();
//   const { itemFor } = useNav();

//   // The page title comes from the nav model, so it can't drift from the menu.
//   // Memoized so the Header prop doesn't churn on every AppShell render.
//   const title = useMemo(
//     () => itemFor(pathname)?.label ?? SHELL.defaultTitle,
//     [itemFor, pathname],
//   );

//   // Stable identity so a memoized Sidebar isn't forced to re-render each
//   // AppShell tick just because we handed it a fresh arrow function.
//   const onToggle = useCallback(() => setCollapsed((c) => !c), [setCollapsed]);

//   // The alerts bell only makes sense for org-scoped users whose org has the
//   // System Monitoring module and who can read alerts — otherwise the summary
//   // endpoint 403s. Gate here so the Header stays chrome-only.
//   const { user } = useAuth();
//   const { can } = useCan();
//   const showAlertsBell =
//     user?.scope_level === "org" &&
//     user?.modules?.sysmon === true &&
//     can("alerts:read");

//   // Viewers only have one sidebar entry (Endpoint Inventory). Rendering a
//   // sidebar for just one item makes the chrome feel abandoned, so we hide the
//   // whole rail and move that single destination into the top-right of the
//   // TopNav (see TopNav.tsx). Non-viewers get the sidebar as a floating overlay
//   // so expanding it slides over the content instead of squeezing the layout.
//   const isViewer = user?.role === "viewer";

//   return (
//     <div className={styles.shell}>
//       {/* Order matters: AutoSelectOrg writes specta.org_id for group admins
//           BEFORE AutoSelectClient reads it (via useEffectiveOrgId) to pick an
//           agent. For org admins AutoSelectOrg is a no-op. */}
//       <AutoSelectOrg />
//       <AutoSelectClient />
//       {/* Refetches every query the moment the selected org or client
//           changes, so the current page always reflects the active
//           (org, client) — see ScopeChangeRefresher for the why. */}
//       <ScopeChangeRefresher />
//       {!isViewer && <Sidebar collapsed={collapsed} onToggle={onToggle} />}

//       <div className={`${styles.main} ${!isViewer ? styles.mainWithRail : ""}`}>
//         <Header title={title} bell={showAlertsBell ? <AlertsBell /> : undefined} showBell={showAlertsBell} />
//         <TopNav />
//         {/* Footer lives INSIDE the scroll container so it sits at the end of
//             the page content rather than pinned to the viewport bottom. When
//             content is short, .contentInner's flex:1 pushes the footer down
//             to the bottom of the viewport anyway; when content overflows, the
//             footer scrolls into view at the end — not visible until you reach it. */}
//         <main className={styles.content}>
//           <div className={styles.contentInner}>{children}</div>
//           <Footer />
//         </main>
//       </div>
//     </div>
//   );
// };


// // /**
// //  * The authenticated chrome, composed once and used by every page:
// //  *
// //  *   ┌──────────┬────────────────────────────────┐
// //  *   │          │            Header              │
// //  *   │ Sidebar  ├────────────────────────────────┤
// //  *   │          │            TopNav              │
// //  *   │          ├────────────────────────────────┤
// //  *   │          │  content  ← the ONE scroller   │
// //  *   │          ├────────────────────────────────┤
// //  *   │          │            Footer              │
// //  *   └──────────┴────────────────────────────────┘
// //  *
// //  * Pages render only their own content — never their own header or scroll
// //  * container. That's why v1 pages had to guess heights like
// //  * `calc(100vh - 140px)`.
// //  */

// // import { useCallback, useMemo } from "react";
// // import { useLocation } from "react-router-dom";
// // import styles from "./AppShell.module.scss";
// // import { Header } from "../chrome/Header";
// // import { Sidebar } from "../navigation/Sidebar";
// // import { TopNav } from "../navigation/TopNav";
// // import { Footer } from "../chrome/Footer";
// // import { useNav } from "../navigation/useNav";
// // import { usePersistentState } from "@shared/hooks/usePersistentState";
// // import { AutoSelectClient } from "@features/endpoint-inventory/AutoSelectClient";
// // import { AutoSelectOrg }    from "@features/endpoint-inventory/AutoSelectOrg";
// // import { AlertsBell } from "@features/resource-alerts/components/AlertsBell";
// // import { useAuth } from "@features/auth/AuthContext";
// // import { useCan } from "@features/auth/hooks/useCan";
// // import { SHELL } from "@shared/config";

// // export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
// //   // Persisted so the sidebar keeps its collapsed/expanded choice across
// //   // reloads. Default false (expanded) on first visit / when nothing is stored.
// //   const [collapsed, setCollapsed] = usePersistentState("sidebar.collapsed", false);
// //   const { pathname } = useLocation();
// //   const { itemFor } = useNav();

// //   // The page title comes from the nav model, so it can't drift from the menu.
// //   // Memoized so the Header prop doesn't churn on every AppShell render.
// //   const title = useMemo(
// //     () => itemFor(pathname)?.label ?? SHELL.defaultTitle,
// //     [itemFor, pathname],
// //   );

// //   // Stable identity so a memoized Sidebar isn't forced to re-render each
// //   // AppShell tick just because we handed it a fresh arrow function.
// //   const onToggle = useCallback(() => setCollapsed((c) => !c), [setCollapsed]);

// //   // The alerts bell only makes sense for org-scoped users whose org has the
// //   // System Monitoring module and who can read alerts — otherwise the summary
// //   // endpoint 403s. Gate here so the Header stays chrome-only.
// //   const { user } = useAuth();
// //   const { can } = useCan();
// //   const showAlertsBell =
// //     user?.scope_level === "org" &&
// //     user?.modules?.sysmon === true &&
// //     can("alerts:read");

// //   // Viewers only have one sidebar entry (Endpoint Inventory). Rendering a
// //   // sidebar for just one item makes the chrome feel abandoned, so we hide the
// //   // whole rail and move that single destination into the top-right of the
// //   // TopNav (see TopNav.tsx). Non-viewers get the sidebar as a floating overlay
// //   // so expanding it slides over the content instead of squeezing the layout.
// //   const isViewer = user?.role === "viewer";

// //   return (
// //     <div className={styles.shell}>
// //       {/* Order matters: AutoSelectOrg writes specta.org_id for group admins
// //           BEFORE AutoSelectClient reads it (via useEffectiveOrgId) to pick an
// //           agent. For org admins AutoSelectOrg is a no-op. */}
// //       <AutoSelectOrg />
// //       <AutoSelectClient />
// //       {!isViewer && <Sidebar collapsed={collapsed} onToggle={onToggle} />}

// //       <div className={`${styles.main} ${!isViewer ? styles.mainWithRail : ""}`}>
// //         <Header title={title} bell={showAlertsBell ? <AlertsBell /> : undefined} showBell={showAlertsBell} />
// //         <TopNav />
// //         {/* Footer lives INSIDE the scroll container so it sits at the end of
// //             the page content rather than pinned to the viewport bottom. When
// //             content is short, .contentInner's flex:1 pushes the footer down
// //             to the bottom of the viewport anyway; when content overflows, the
// //             footer scrolls into view at the end — not visible until you reach it. */}
// //         <main className={styles.content}>
// //           <div className={styles.contentInner}>{children}</div>
// //           <Footer />
// //         </main>
// //       </div>
// //     </div>
// //   );
// // };

